import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Check, Trash2, Plus } from "lucide-react";

export const TemplateEditor = ({ open, onOpenChange, templates, onSaved }) => {
  const [items, setItems] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newHandle, setNewHandle] = useState(false);
  useEffect(() => { if (open) setItems(templates.map((t) => ({ ...t }))); }, [open, templates]);

  const saveOne = async (t) => {
    try { await api.put(`/admin/reply-templates/${t.id}`, { label: t.label, body: t.body, handle: t.handle }); toast.success("Template saved"); onSaved(); }
    catch (e) { toast.error(errText(e)); }
  };
  const removeOne = async (id) => {
    try { await api.delete(`/admin/reply-templates/${id}`); toast.success("Template removed"); onSaved(); }
    catch (e) { toast.error(errText(e)); }
  };
  const addOne = async () => {
    if (!newLabel.trim() || !newBody.trim()) return toast.error("Add a label and message");
    try {
      await api.post("/admin/reply-templates", { label: newLabel.trim(), body: newBody.trim(), handle: newHandle });
      setNewLabel(""); setNewBody(""); setNewHandle(false);
      toast.success("Template added"); onSaved();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle>Edit quick-reply templates</DialogTitle>
          <DialogDescription>Use <code className="text-[#F5551E]">{"{first}"}</code> to insert the sender's first name. "Mark handled" closes the message when this reply is used.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {items.map((t, i) => (
            <div key={t.id} className="rounded-xl border border-slate-200 p-3" data-testid={`template-edit-${t.id}`}>
              <div className="flex items-center gap-2">
                <Input value={t.label} onChange={(e) => setItems((arr) => arr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label" data-testid={`template-label-${t.id}`} className="rounded-lg h-9 font-semibold" maxLength={40} />
                <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
                  <Switch checked={t.handle} onCheckedChange={(v) => setItems((arr) => arr.map((x, j) => j === i ? { ...x, handle: v } : x))} data-testid={`template-handle-${t.id}`} /> Mark handled
                </label>
              </div>
              <Textarea value={t.body} onChange={(e) => setItems((arr) => arr.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} data-testid={`template-body-${t.id}`} className="rounded-lg mt-2 min-h-[90px] text-sm" maxLength={2000} />
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" onClick={() => saveOne(items[i])} data-testid={`template-save-${t.id}`} className="rounded-lg h-8 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1"><Check size={14} /> Save</Button>
                <Button size="sm" variant="ghost" onClick={() => removeOne(t.id)} data-testid={`template-delete-${t.id}`} className="rounded-lg h-8 text-red-500 hover:text-red-600 gap-1"><Trash2 size={14} /> Remove</Button>
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-dashed border-slate-300 p-3" data-testid="template-add">
            <p className="text-xs font-semibold text-slate-500 mb-2">Add a new template</p>
            <div className="flex items-center gap-2">
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (e.g. Spam)" data-testid="template-new-label" className="rounded-lg h-9" maxLength={40} />
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap"><Switch checked={newHandle} onCheckedChange={setNewHandle} data-testid="template-new-handle" /> Mark handled</label>
            </div>
            <Textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="Reply message… use {first} for the sender's name" data-testid="template-new-body" className="rounded-lg mt-2 min-h-[80px] text-sm" maxLength={2000} />
            <Button size="sm" onClick={addOne} data-testid="template-new-add" className="rounded-lg h-8 mt-2 gap-1"><Plus size={14} /> Add template</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
